import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, FlaskConical } from "lucide-react";
import { api } from "@/lib/api";
import { ukDay, ukTime } from "@/lib/time";
import { Panel, Chips, CellSub, Empty, StatStrip, Tb, Th, Td, CellMain } from "@/components/portal/crm/kit";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const KINDS = ["Introducer", "Call centre", "Media partner", "Lender"];

/** Meet Connie — preview the intro email per partner type, test it to yourself, then send it once to everyone. */
export function MeetConnie() {
  const [st, setSt] = useState(null);
  const [kind, setKind] = useState("Introducer");
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    try { setSt((await api.get("/hub/connie/announce")).data); } catch { toast.error("Couldn't load the intro email"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/hub/connie/announce/preview", { params: { kind } }).then((r) => setHtml(r.data.html)).catch(() => setHtml(""));
  }, [kind]);
  useEffect(() => {
    if (!st?.run?.sending) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [st, load]);

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/hub/connie/announce/test", { kind });
      if (data.ok) toast.success(`Test sent to ${data.to}`, { description: `The ${kind.toLowerCase()} version.` });
      else toast.error("The test didn't send");
    } catch { toast.error("The test didn't send"); }
    finally { setBusy(""); }
  };
  const send = async () => {
    setBusy("send");
    try {
      const { data } = await api.post("/hub/connie/announce/send");
      toast.success(`Sending to ${data.queued} partner${data.queued === 1 ? "" : "s"}`, { description: "It paces itself — you can leave this page." });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't send"); }
    finally { setBusy(""); setConfirm(false); }
  };

  if (!st) return <Empty testId="hub-announce-loading">Loading…</Empty>;
  const run = st.run || {};
  return (
    <div data-testid="hub-announce">
    <Results key={`${run.finished_at || ""}${run.sent || 0}`} />
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
      <Panel title="What partners will see" sub={st.subject} testId="hub-announce-preview-panel">
        <Chips testPrefix="hub-announce-kind" value={kind} onChange={setKind} items={KINDS.map((k) => ({ key: k, label: k }))} />
        <iframe title="Meet Connie email preview" srcDoc={html} data-testid="hub-announce-preview"
                style={{ width: "100%", height: 720, border: "1px solid var(--line)", borderRadius: 12, marginTop: 14, background: "#f1f5f9" }} />
      </Panel>
      <div className="space-y-5">
        <Panel title="Who gets it" sub="Every active partner with an email. Demo accounts are left out, and nobody gets it twice." testId="hub-announce-who">
          {KINDS.map((k) => (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, padding: "7px 0", borderBottom: "1px solid var(--line)" }}
                 data-testid={`hub-announce-count-${k.replace(/\s/g, "-").toLowerCase()}`}>
              <span>{k}s</span><span><b>{st.by_kind[k]?.to_send || 0}</b> <span style={{ color: "var(--mut)" }}>of {st.by_kind[k]?.total || 0}</span></span>
            </div>
          ))}
          <CellSub style={{ marginTop: 12 }} data-testid="hub-announce-summary">
            {st.to_send ? `${st.to_send} still to send · ${st.already_sent} already had it` : `All ${st.total} partners have had it.`}
          </CellSub>
        </Panel>
        <Panel title="Send it" testId="hub-announce-send-panel">
          <button className="btn btn-ghost" style={{ width: "100%", justifyContent: "center" }} onClick={test} disabled={!!busy} data-testid="hub-announce-test">
            {busy === "test" ? <Loader2 className="animate-spin" /> : <><FlaskConical /> Send me a test ({kind})</>}
          </button>
          <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: 10 }}
                  onClick={() => setConfirm(true)} disabled={!!busy || !st.to_send || run.sending} data-testid="hub-announce-send">
            <Send /> {run.sending ? "Sending…" : st.to_send ? `Send to ${st.to_send} partner${st.to_send === 1 ? "" : "s"}` : "Everyone's had it"}
          </button>
          {(run.sending || run.finished_at) && (
            <CellSub style={{ marginTop: 12 }} data-testid="hub-announce-run">
              {run.sending ? `Sending ${run.sent || 0} of ${run.total}…`
                : `Last sent ${ukDay(run.finished_at)} ${ukTime(run.finished_at)} by ${run.by}: ${run.sent} delivered${run.failed ? `, ${run.failed} failed — press send again to retry them` : ""}.`}
            </CellSub>
          )}
        </Panel>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="hub-announce-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Send Meet Connie to {st.to_send} partners?</AlertDialogTitle>
            <AlertDialogDescription>
              Each partner gets the version for their type, from The Connexis desk. Replies land in Messages and Connie answers them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hub-announce-confirm-cancel">Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={send} data-testid="hub-announce-confirm-send">Send it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </div>
  );
}

const pct = (v) => (v == null ? "—" : `${v}%`);
const when = (iso) => (iso ? `${ukDay(iso)} ${ukTime(iso)}` : "—");

/** How the intro landed: opens, clicks through to Ask Connie, and who actually talked to her afterwards. */
function Results() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/hub/connie/announce/results").then((x) => setR(x.data)).catch(() => setR(null)); }, []);
  if (!r || !r.total.sent) return null;
  const t = r.total;
  return (
    <div style={{ marginBottom: 22 }} data-testid="hub-announce-results">
      <StatStrip testId="hub-announce-results-stats" items={[
        { k: "Sent", v: t.sent, h: "partners emailed", testId: "hub-announce-results-sent" },
        { k: "Opened", v: t.opened, h: `${pct(t.opened_pct)} · Apple Mail can over-count`, testId: "hub-announce-results-opened" },
        { k: "Clicked Ask Connie", v: t.clicked, h: pct(t.clicked_pct), testId: "hub-announce-results-clicked" },
        { k: "Talked to Connie", v: t.talked, h: `${pct(t.talked_pct)} · portal, email or text`, testId: "hub-announce-results-talked" },
      ]} />
      <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]" style={{ marginTop: 16 }}>
        <Panel title="By partner type" testId="hub-announce-results-kinds">
          {Object.entries(r.by_kind).filter(([, v]) => v.sent).map(([k, v]) => (
            <div key={k} style={{ padding: "9px 0", borderBottom: "1px solid var(--line)" }} data-testid={`hub-announce-results-kind-${k.replace(/\s/g, "-").toLowerCase()}`}>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{k}s <span style={{ color: "var(--mut)", fontWeight: 500 }}>· {v.sent} sent</span></div>
              <CellSub style={{ margin: "3px 0 0" }}>{pct(v.opened_pct)} opened · {pct(v.clicked_pct)} clicked · {pct(v.talked_pct)} talked to Connie</CellSub>
            </div>
          ))}
        </Panel>
        <Panel pad={false} title="Who did what" sub="People who talked to Connie first, then clicks, then opens." testId="hub-announce-results-list">
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            <Tb head={<><Th>Partner</Th><Th>Opened</Th><Th>Clicked</Th><Th className="r">Talked to Connie</Th></>}>
              {r.items.map((x) => (
                <tr key={`${x.kind}-${x.party_id}`} data-testid={`hub-announce-results-row-${x.party_id}`}>
                  <Td className="lead"><CellMain>{x.company}</CellMain><CellSub>{x.label} · {x.contact}</CellSub></Td>
                  <Td><span style={{ color: x.opened_at ? "inherit" : "var(--mut)" }}>{when(x.opened_at)}</span></Td>
                  <Td><span style={{ color: x.clicked_at ? "inherit" : "var(--mut)" }}>{when(x.clicked_at)}</span></Td>
                  <Td className="r">{x.talked_at ? <><b>{x.talked_via}</b><CellSub style={{ margin: 0 }}>{when(x.talked_at)}</CellSub></> : <span style={{ color: "var(--mut)" }}>Not yet</span>}</Td>
                </tr>
              ))}
            </Tb>
          </div>
        </Panel>
      </div>
    </div>
  );
}

export default MeetConnie;
